import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Heart, Check, AlertCircle } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Unsubscribe() {
  usePageMeta({ title: "Unsubscribe — Hope Chest", description: "Update your email preferences." });
  const [params] = useSearchParams();
  const initialEmail = params.get("email") || "";
  const [email, setEmail] = useState(initialEmail);
  const [status, setStatus] = useState("idle"); // idle | submitting | done | error
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email || !email.includes("@")) {
      setErrorMsg("Please enter a valid email address.");
      setStatus("error");
      return;
    }
    setStatus("submitting");
    setErrorMsg("");
    try {
      await axios.post(`${API}/public/unsubscribe`, { email });
      setStatus("done");
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || "Could not process — please try again or email support@organizedhope.online.");
      setStatus("error");
    }
  };

  // Auto-submit when email is supplied via URL (one-click flow from email footer)
  useEffect(() => {
    if (initialEmail && status === "idle") {
      submit();
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#FCFAF8] flex items-center justify-center px-6 py-12 relative overflow-hidden">
      <div className="hc-blob" style={{ background: "#E8DCC8", width: 380, height: 380, top: -120, right: -80 }} />
      <div className="hc-blob" style={{ background: "#D8E2D8", width: 320, height: 320, bottom: -100, left: -100 }} />

      <div className="relative z-10 max-w-md w-full hc-card p-8" data-testid="unsubscribe-card">
        <div className="w-12 h-12 rounded-full bg-[#F3EFEA] flex items-center justify-center mb-4">
          <Heart className="w-5 h-5 text-[#C87B67]" fill="#C87B67" />
        </div>

        {status === "done" && (
          <>
            <h1 className="font-display text-2xl mb-2" data-testid="unsubscribe-success-title">You&apos;re unsubscribed.</h1>
            <p className="text-sm text-[#7A736E] mb-4">
              We&apos;ve removed <strong className="text-[#3D3935]">{email}</strong> from product updates and drip emails. You&apos;ll still receive important account emails (password resets, billing receipts) and any SMS reminders you set up.
            </p>
            <div className="rounded-2xl bg-[#F3EFEA] p-4 mb-5 flex gap-3">
              <Check className="w-4 h-4 text-[#3A5A4A] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#3D3935]">
                Changed your mind? Sign in and turn product updates back on in your profile settings.
              </p>
            </div>
            <Link
              to="/"
              className="inline-flex items-center justify-center w-full rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] text-white px-6 py-3 text-sm font-medium transition"
              data-testid="unsubscribe-back-home"
            >
              Back to Hope Chest
            </Link>
          </>
        )}

        {status !== "done" && (
          <>
            <h1 className="font-display text-2xl mb-2">Unsubscribe from updates</h1>
            <p className="text-sm text-[#7A736E] mb-5">
              Enter your email to stop receiving product updates and drip campaigns. Account emails (password resets, billing) will still be sent.
            </p>
            <form onSubmit={submit} noValidate className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-xl border border-[#EBE5DF] focus:border-[#3A5A4A] focus:outline-none text-sm"
                data-testid="unsubscribe-email-input"
                disabled={status === "submitting"}
              />
              {status === "error" && (
                <div className="flex items-start gap-2 text-sm text-[#C87B67]" data-testid="unsubscribe-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{errorMsg}</span>
                </div>
              )}
              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] disabled:opacity-60 text-white px-6 py-3 text-sm font-medium transition"
                data-testid="unsubscribe-submit"
              >
                {status === "submitting" ? "Unsubscribing…" : "Unsubscribe"}
              </button>
            </form>
            <p className="text-xs text-[#7A736E] mt-4 text-center">
              Questions? <Link to="/support" className="underline hover:text-[#3D3935]">Contact support</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
